// Move input (spec/move-input-spec.md): turns clicks, drags and keys into
// exactly one legal move. Beginner (§5): the piece, then every landing, one
// jump at a time. Advanced (§6): every click narrows the candidate routes; one
// move left is played, and the piece walks along what the rest share.
// Slips (§7): clicks that fit no legal move are ignored, and a completed move
// is pending for 0.5 s, in which another move replaces it.
//
// No DOM here. The board calls the input methods with square numbers and the
// time, renders `view()`, and carries out the drained `effects`.
import type { LegalMove } from "../api/client";
import {
  buildRoutes,
  commonPrefix,
  contentOf,
  isJump,
  jumped,
  movesOf,
  onRay,
  startsWith,
  type Content,
  type Route,
} from "./routes";

export type Assist = "beginner" | "advanced";

export type HintCode = "must_capture" | "most_pieces" | "one_jump" | "piece_first" | "cannot_move" | "finish_first" | "choose";

export type Effect =
  | { kind: "hint"; code: HintCode; n?: number }
  | { kind: "pulse"; squares: number[] }
  | { kind: "shake" }
  /** Animate `move` along `route`, from the piece standing on `route[from]`. */
  | { kind: "show"; move: LegalMove; route: number[]; from: number }
  /** Undo the shown pending move on the board. */
  | { kind: "takeBack" }
  /** A drag that did not fit anything: the piece returns. */
  | { kind: "flyBack" }
  /** The move counts: send it. */
  | { kind: "commit"; move: LegalMove; enteredAt: number; replaced: string[] };

export type View = {
  /** The square of the selected or moving piece. */
  selected: number | null;
  /** Where a part-way capture has brought the piece (it is off its origin). */
  at: number | null;
  origin: number | null;
  /** The squares walked so far: origin, then landings. */
  prefix: number[];
  faded: number[];
  dots: number[];
  rings: number[];
  /** Choice mode (§6 A4): squares that settle the choice, and the routes. */
  outlines: number[];
  lines: number[][];
};

export const GRACE_MS = 500;
export const HOLD_MS = 2000;

type Entry = { cands: Route[]; prefix: number[] };
type Pending = { move: LegalMove; route: number[]; enteredAt: number; replaced: string[]; shown: boolean };
type Drag = { snapshot: Entry | null; start: Entry | null; press: number; visited: number[]; moved: boolean };

const EMPTY_VIEW: View = { selected: null, at: null, origin: null, prefix: [], faded: [], dots: [], rings: [], outlines: [], lines: [] };

export class MoveInput {
  readonly all: Route[];
  entry: Entry | null = null;
  pending: Pending | null = null;
  private drag: Drag | null = null;
  private shakes = 0;
  private out: Effect[] = [];

  constructor(
    readonly textfen: string,
    legal: LegalMove[],
    readonly assist: Assist,
  ) {
    this.all = buildRoutes(textfen, legal);
  }

  // -- public input ----------------------------------------------------------

  /** A click (press and release in one place), or a press with dragging off. */
  click(sq: number | null, now: number): Effect[] {
    if (sq !== null) {
      if (this.assist === "beginner") this.beginnerClick(sq, now);
      else this.advancedClick(sq, now);
    }
    return this.settle(now);
  }

  /** Can a drag start on this square? (§4: the piece that can move.) */
  draggable(sq: number): boolean {
    const p = this.entry?.prefix ?? [];
    if (p.length > 1) return sq === p[p.length - 1];
    return this.all.some((r) => r.sq[0] === sq);
  }

  /** Press with dragging on. Not draggable: acts as a click at once. */
  press(sq: number, now: number): Effect[] {
    if (!this.draggable(sq)) return this.click(sq, now);
    const snapshot = this.copy();
    const p = this.entry?.prefix ?? [];
    if (!(p.length > 1 && sq === p[p.length - 1]) && !(p.length === 1 && p[0] === sq)) {
      // select without playing, even when the piece has one move (§6 A5)
      this.entry = { cands: this.all.filter((r) => r.sq[0] === sq), prefix: [sq] };
    }
    this.drag = { snapshot, start: this.copy(), press: sq, visited: [], moved: false };
    return this.settle(now);
  }

  /** The pointer moved beyond the drag threshold. */
  dragStart(): void {
    if (this.drag) this.drag.moved = true;
  }

  get dragging(): boolean {
    return !!this.drag?.moved;
  }

  /** The dragged pointer entered the centre zone of `sq`. */
  enter(sq: number, now: number): Effect[] {
    const d = this.drag;
    if (!d || !d.moved) return [];
    const cur = this.replay(d.visited);
    const before = d.visited.length ? this.replay(d.visited.slice(0, -1)) : null;
    if (before && before.prefix[before.prefix.length - 1] === sq && !before.prefix.slice(0, -1).includes(sq)) {
      d.visited.pop(); // back into the previous landing: that step is undone
    } else if (cur && this.nextLandings(cur).includes(sq) && !cur.prefix.includes(sq)) {
      d.visited.push(sq);
    } else if (cur && before && d.visited.length) {
      // a king: the last landing visited on the same line counts (§5 B3)
      const alt = [...d.visited.slice(0, -1), sq];
      const prev = before.prefix[before.prefix.length - 1];
      if (this.replay(alt) && onRay(prev, d.visited[d.visited.length - 1], sq)) d.visited = alt;
    }
    this.entry = this.replay(d.visited);
    return this.settle(now);
  }

  /** Release with dragging on. `sq` null: off the board. */
  release(sq: number | null, now: number): Effect[] {
    const d = this.drag;
    if (!d) return this.settle(now);
    this.drag = null;
    if (!d.moved || (sq === d.press && !d.visited.length)) {
      this.entry = d.snapshot;
      return this.click(sq, now);
    }
    if (sq === null) {
      this.flyBack(d);
      return this.settle(now);
    }
    const e = this.entry!;
    const p = e.prefix.length;
    if (this.assist === "beginner") {
      const next = e.cands.filter((r) => r.sq[p] === sq);
      if (next.length) {
        this.entry = { cands: next, prefix: [...e.prefix, sq] };
        if (next[0].sq.length === p + 1) this.complete(next[0], p, now);
      } else if (sq === e.prefix[p - 1] && d.visited.length) {
        if (e.cands[0].sq.length === p) this.complete(e.cands[0], p - 1, now);
      } else {
        const hint = this.beginnerMiss(e, sq);
        this.flyBack(d);
        if (hint) this.out.push(hint);
      }
      return this.settle(now);
    }
    if (e.prefix.includes(sq) && d.visited.length) {
      const moves = movesOf(e.cands);
      if (moves.length === 1) this.complete(e.cands[0], e.cands[0].sq.lastIndexOf(sq), now);
      return this.settle(now);
    }
    const fits = this.roles(e, sq);
    if (!fits.length) {
      this.flyBack(d);
      return this.settle(now);
    }
    this.narrow(fits, now, sq);
    return this.settle(now);
  }

  /** The drag was abandoned (pointer cancelled, left the window): back to the press. */
  cancelDrag(now: number): Effect[] {
    const d = this.drag;
    if (d) {
      this.drag = null;
      this.flyBack(d);
    }
    return this.settle(now);
  }

  /** `Escape` cancels; `Backspace` (and right-click) undoes a step (§4). */
  key(name: "Escape" | "Backspace", now: number): Effect[] {
    if (this.drag) {
      const d = this.drag;
      this.drag = null;
      this.entry = d.snapshot;
      if (d.moved) this.out.push({ kind: "flyBack" });
    }
    const p = this.entry?.prefix ?? [];
    if (name === "Backspace" && this.assist === "beginner" && p.length > 1) {
      const prefix = p.slice(0, -1);
      this.entry = { cands: this.all.filter((r) => startsWith(r, prefix)), prefix };
    } else this.cancel();
    return this.settle(now);
  }

  private cancel(): void {
    this.entry = null;
    if (this.pending && !this.pending.shown) this.commit(); // a cancelled correction: the pending move counts
  }

  /** A typed move (`28x8`, `28x19x8`): origin, landings in order, destination. */
  typed(text: string, now: number): Effect[] {
    const sq = text
      .split(/[^0-9]+/)
      .filter(Boolean)
      .map(Number);
    if (sq.length >= 2) {
      const fits = this.all.filter((r) => {
        if (r.sq[0] !== sq[0] || r.sq[r.sq.length - 1] !== sq[sq.length - 1]) return false;
        let i = 1;
        for (const s of sq.slice(1, -1)) {
          i = r.sq.indexOf(s, i);
          if (i < 0) return false;
          i++;
        }
        return true;
      });
      if (fits.length) {
        this.entry = null;
        this.narrow(fits, now);
      }
    }
    return this.settle(now);
  }

  /** Time passes: the pending move counts after the grace period (§7 G2). */
  tick(now: number): Effect[] {
    const pd = this.pending;
    if (!pd) return [];
    if (!this.entry && !this.drag && now - pd.enteredAt >= GRACE_MS) this.commit();
    else if ((this.entry || this.drag) && now - pd.enteredAt >= HOLD_MS) {
      if (this.drag?.moved) this.out.push({ kind: "flyBack" });
      this.drag = null;
      this.entry = null;
      this.commit();
    }
    return this.drain();
  }

  /** When `tick` next has something to do, or null. */
  deadline(): number | null {
    const pd = this.pending;
    if (!pd) return null;
    return pd.enteredAt + (this.entry || this.drag ? HOLD_MS : GRACE_MS);
  }

  view(): View {
    const e = this.entry;
    if (!e) return EMPTY_VIEW;
    const P = e.prefix;
    const p = P.length;
    const dots = new Set<number>();
    const rings = new Set<number>();
    if (p >= 1) {
      for (const r of e.cands) {
        if (p >= r.sq.length) continue;
        const last = r.sq[r.sq.length - 1];
        if (p === r.sq.length - 1) (r.caps.length ? rings : dots).add(last);
        else {
          dots.add(r.sq[p]);
          rings.add(last);
        }
      }
    }
    const choice = this.choice(e);
    return {
      selected: p ? P[p - 1] : null,
      at: p > 1 ? P[p - 1] : null,
      origin: p ? P[0] : null,
      prefix: P,
      faded: jumped(e.cands, P),
      dots: [...dots].filter((s) => !rings.has(s)),
      rings: [...rings],
      outlines: choice?.outlines ?? [],
      lines: choice?.lines ?? [],
    };
  }

  // -- beginner (§5) ---------------------------------------------------------

  private beginnerClick(x: number, now: number): void {
    const e = this.entry;
    const P = e?.prefix ?? [];
    const p = P.length;
    const c = this.content(x);
    if (!e || p === 0) {
      if (c === "own") this.selectPiece(x);
      else this.hint("piece_first");
      return;
    }
    const next = e.cands.filter((r) => r.sq[p] === x);
    if (next.length) {
      this.entry = { cands: next, prefix: [...P, x] };
      if (next[0].sq.length === p + 1) this.complete(next[0], p, now);
      return;
    }
    if (p > 1 && x === P[p - 2]) {
      const prefix = P.slice(0, -1);
      this.entry = { cands: this.all.filter((r) => startsWith(r, prefix)), prefix };
      return;
    }
    if (c === "own") {
      if (p === 1 && x === P[0]) this.entry = null;
      else if (p === 1) this.selectPiece(x);
      else this.hint("finish_first");
      return;
    }
    const miss = this.beginnerMiss(e, x);
    if (miss) this.out.push(miss);
    else if (p > 1) this.shake();
    // otherwise ignored: the piece stays selected (§7 G1)
  }

  /** The learning hint for a beginner's square that is not the next landing. */
  private beginnerMiss(e: Entry, x: number): Effect | null {
    const P = e.prefix;
    const p = P.length;
    const c = this.content(x);
    if (e.cands.some((r) => r.sq.indexOf(x, p + 1) > p)) return { kind: "hint", code: "one_jump" };
    if (c === "opp" && e.cands.some((r) => r.caps.indexOf(x, p - 1) >= 0)) return { kind: "hint", code: "one_jump" };
    const king = this.textfen[P[0]] === "W" || this.textfen[P[0]] === "B";
    const max = e.cands[0]?.caps.length ?? 0;
    if (c !== "opp" && max && isJump(this.textfen, P[p - 1], x, jumped(e.cands, P), king, P[0]))
      return { kind: "hint", code: "most_pieces", n: max };
    return null;
  }

  // -- advanced (§6) ---------------------------------------------------------

  private advancedClick(x: number, now: number): void {
    const e = this.entry;
    const P = e?.prefix ?? [];
    const p = P.length;
    if (e && p === 1 && x === P[0]) {
      // the selected piece again: a round trip back to it, or deselect (A3)
      const back = e.cands.filter((r) => r.sq.indexOf(x, 1) > 0);
      if (back.length) this.narrow(back, now);
      else this.entry = null;
      return;
    }
    const fits = e ? this.roles(e, x) : this.roles({ cands: this.all, prefix: [] }, x);
    if (fits.length) {
      this.narrow(fits, now);
      return;
    }
    if (e && p <= 1) {
      // perhaps another piece's move was meant: try on the full set (A2)
      const again = this.roles({ cands: this.all, prefix: [] }, x);
      if (again.length) {
        this.entry = null;
        this.narrow(again, now);
        return;
      }
    }
    if (p > 1 && x === P[0]) {
      this.cancel(); // the empty origin of a capture on its way (§4)
      return;
    }
    if (this.content(x) === "own") {
      if (!this.all.some((r) => r.sq[0] === x)) this.noMoveHint();
      return;
    }
    if (p > 1) {
      this.shake();
      if (++this.shakes >= 2) this.hint("finish_first");
    }
    // otherwise ignored, selection kept (§7 G1)
  }

  /** A2: the candidates in which `x` plays a role that fits what stands on it. */
  private roles(e: Entry, x: number): Route[] {
    const P = e.prefix;
    const p = P.length;
    const c = this.content(x);
    if (c === "own") {
      if (p > 1 || (p === 1 && x !== P[0])) return this.all.filter((r) => r.sq[0] === x);
      return e.cands.filter((r) => r.sq[0] === x);
    }
    if (c === "empty") {
      const k = Math.max(p, 1);
      const next = e.cands.filter((r) => r.sq[k] === x);
      return next.length ? next : e.cands.filter((r) => r.sq.indexOf(x, k) >= k);
    }
    const k = Math.max(p - 1, 0);
    const next = e.cands.filter((r) => r.caps[k] === x);
    return next.length ? next : e.cands.filter((r) => r.caps.indexOf(x, k) >= k);
  }

  /** One move left: play it. Otherwise narrow and walk the shared part. */
  private narrow(fits: Route[], now: number, at?: number): void {
    const moves = movesOf(fits);
    const p = this.entry?.prefix.length ?? 0;
    if (moves.length === 1) {
      const r = fits[0];
      const from = at !== undefined && r.sq.includes(at) ? r.sq.lastIndexOf(at) : Math.max(p - 1, 0);
      this.complete(r, from, now);
      return;
    }
    const prefix = commonPrefix(fits);
    this.entry = { cands: fits, prefix };
    if (!prefix.length) this.out.push({ kind: "pulse", squares: [...new Set(fits.map((r) => r.sq[0]))] });
    if (this.choice(this.entry)) this.hint("choose");
  }

  /** Choice mode: every candidate ends on one square, as two or more moves. */
  private choice(e: Entry): { outlines: number[]; lines: number[][] } | null {
    const moves = movesOf(e.cands);
    if (moves.length < 2) return null;
    if (new Set(e.cands.map((r) => r.sq[r.sq.length - 1])).size !== 1) return null;
    const p = e.prefix.length;
    const per = moves.map((m) => {
      const own = new Set<number>();
      for (const r of e.cands.filter((x) => x.move === m)) {
        r.sq.slice(Math.max(p - 1, 0), -1).forEach((s) => own.add(s));
        r.caps.slice(Math.max(p - 1, 0)).forEach((s) => own.add(s));
      }
      return own;
    });
    const outlines = new Set<number>();
    per.forEach((set) => set.forEach((s) => per.some((o) => !o.has(s)) && outlines.add(s)));
    const lines = moves.map((m) => e.cands.find((r) => r.move === m)!.sq);
    return { outlines: [...outlines].filter((s) => !e.prefix.includes(s)), lines };
  }

  // -- shared ----------------------------------------------------------------

  private selectPiece(x: number): void {
    const mine = this.all.filter((r) => r.sq[0] === x);
    if (mine.length) this.entry = { cands: mine, prefix: [x] };
    else this.noMoveHint();
  }

  private noMoveHint(): void {
    if (this.all.some((r) => r.move.captured.length)) {
      this.hint("must_capture");
      this.out.push({ kind: "pulse", squares: [...new Set(this.all.map((r) => r.sq[0]))] });
    } else this.hint("cannot_move");
  }

  private complete(route: Route, from: number, now: number): void {
    const move = route.move;
    this.entry = null;
    this.shakes = 0;
    const pd = this.pending;
    if (pd && pd.move === move) {
      // the same move again: it counts at once
      if (!pd.shown) this.out.push({ kind: "show", move, route: pd.route, from: 0 });
      this.commit();
      return;
    }
    let replaced: string[] = [];
    if (pd) {
      replaced = [...pd.replaced, pd.move.move];
      if (pd.shown) this.out.push({ kind: "takeBack" });
    }
    this.out.push({ kind: "show", move, route: route.sq, from });
    this.pending = { move, route: route.sq, enteredAt: now, replaced, shown: true };
  }

  private commit(): void {
    const pd = this.pending;
    if (!pd) return;
    if (!pd.shown) this.out.push({ kind: "show", move: pd.move, route: pd.route, from: 0 });
    this.out.push({ kind: "commit", move: pd.move, enteredAt: pd.enteredAt, replaced: pd.replaced });
    this.pending = null;
  }

  private flyBack(d: Drag): void {
    this.entry = d.snapshot;
    this.out.push({ kind: "flyBack" });
  }

  /** The drag state after visiting `visited`, from the state at the press. */
  private replay(visited: number[]): Entry | null {
    let e: Entry | null = this.drag?.start ?? null;
    if (!e) return null;
    for (const v of visited) {
      const p = e.prefix.length;
      const k = Math.max(p, 1);
      const next: Route[] = e.cands.filter((r) => r.sq[k] === v);
      if (!next.length) return null;
      e = { cands: next, prefix: this.assist === "beginner" ? [...e.prefix, v] : commonPrefix(next) };
    }
    return e;
  }

  private nextLandings(e: Entry): number[] {
    const k = Math.max(e.prefix.length, 1);
    return e.cands.map((r) => r.sq[k]).filter((s) => s !== undefined);
  }

  private content(x: number): Content {
    const P = this.entry?.prefix ?? [];
    if (P.length > 1 && x === P[0]) return "empty"; // the origin is empty during a capture
    return contentOf(this.textfen, x);
  }

  private copy(): Entry | null {
    return this.entry ? { cands: this.entry.cands, prefix: [...this.entry.prefix] } : null;
  }

  private hint(code: HintCode, n?: number): void {
    this.out.push(n === undefined ? { kind: "hint", code } : { kind: "hint", code, n });
  }

  private shake(): void {
    this.out.push({ kind: "shake" });
  }

  /** After every input: an entry started during the grace period takes the
   * pending move back off the board (§7 G2). */
  private settle(_now: number): Effect[] {
    const pd = this.pending;
    if (pd && pd.shown && (this.entry || this.drag)) {
      this.out.unshift({ kind: "takeBack" });
      pd.shown = false;
    } else if (pd && !pd.shown && !this.entry && !this.drag) {
      // the correction came to nothing: the pending move is back on the board
      this.out.push({ kind: "show", move: pd.move, route: pd.route, from: pd.route.length - 1 });
      pd.shown = true;
    }
    if (!this.entry) this.shakes = 0;
    return this.drain();
  }

  private drain(): Effect[] {
    const out = this.out;
    this.out = [];
    return out;
  }
}
